// pages/History.jsx
import { useState, useEffect } from "react";
import { T } from "../Tokens";
import { USER_ID, API_BASE } from "../config";
import { Chip, Skeleton } from "../components/UI";

// ── helpers ──────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
    "  " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
  );
}

function relTime(iso) {
  if (!iso) return "";
  const m = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const verdictVariant = (v) =>
  ({ TRUE: "green", FALSE: "red", UNVERIFIED: "amber" })[v?.toUpperCase()] ||
  "amber";

// ── StatsBar ─────────────────────────────────────────────────────
function StatsBar({ sessions }) {
  const totalMsgs = sessions.reduce((a, s) => a + (s.messages?.length || 0), 0);
  const totalVerify = sessions.reduce(
    (a, s) =>
      a +
      (s.messages || []).filter(
        (m) => m.role === "assistant" && m.payload?.intent === "VERIFY",
      ).length,
    0,
  );
  const totalTrue = sessions.reduce(
    (a, s) =>
      a +
      (s.messages || []).filter(
        (m) => m.payload?.verification?.verdict === "TRUE",
      ).length,
    0,
  );
  const totalFalse = sessions.reduce(
    (a, s) =>
      a +
      (s.messages || []).filter(
        (m) => m.payload?.verification?.verdict === "FALSE",
      ).length,
    0,
  );

  const stats = [
    { label: "Sessions", value: sessions.length, color: T.textSecondary },
    { label: "Messages", value: totalMsgs, color: T.textSecondary },
    { label: "Verified", value: totalVerify, color: T.amberText },
    { label: "True", value: totalTrue, color: T.greenText },
    { label: "False", value: totalFalse, color: T.redText },
  ];

  return (
    <div
      className="stats-grid"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: 1,
        background: T.border,
        borderRadius: T.radius,
        overflow: "hidden",
        marginBottom: 16,
        flexShrink: 0,
      }}
    >
      {stats.map(({ label, value, color }) => (
        <div
          key={label}
          style={{
            padding: "12px 10px",
            textAlign: "center",
            background: T.bg1,
          }}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 500,
              color,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1,
            }}
          >
            {value}
          </div>
          <div
            style={{
              fontSize: 9,
              color: T.textDim,
              marginTop: 4,
              letterSpacing: "0.05em",
            }}
          >
            {label}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── SessionCard ──────────────────────────────────────────────────
// ── SessionCard ──────────────────────────────────────────────────
function SessionCard({ session, onOpen, onDelete, index }) {
  const msgCount = session.messages?.length || 0;

  const verdicts = [
    ...new Set(
      (session.messages || [])
        .filter(
          (m) => m.role === "assistant" && m.payload?.verification?.verdict,
        )
        .map((m) => m.payload.verification.verdict.toUpperCase()),
    ),
  ];

  const lastUserMsg = [...(session.messages || [])]
    .reverse()
    .find((m) => m.role === "user");

  return (
    <div
      className="session-card"
      onClick={() => onOpen(session.sessionId)}
      style={{
        animation: `fadeUp 0.25s ease ${index * 0.04}s both`,
        paddingLeft: 18,
        position: "relative",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        {/* Icon */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: T.radius,
            flexShrink: 0,
            background: T.bg3,
            border: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill={T.textDim}>
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
          </svg>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: id + chips */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              flexWrap: "wrap",
              marginBottom: 4,
            }}
          >
            <span
              style={{ fontSize: 12, fontWeight: 500, color: T.textPrimary }}
            >
              Session #{session.sessionId}
            </span>
            <span
              style={{
                fontSize: 9,
                color: T.textDim,
                background: T.bg3,
                border: `1px solid ${T.border}`,
                borderRadius: T.radiusSm,
                padding: "1px 6px",
              }}
            >
              {msgCount} msg{msgCount !== 1 ? "s" : ""}
            </span>
            {verdicts.map((v) => (
              <Chip key={v} variant={verdictVariant(v)}>
                {v}
              </Chip>
            ))}
          </div>

          {/* Row 2: last message preview */}
          {lastUserMsg && (
            <div
              style={{
                fontSize: 11,
                color: T.textDim,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginBottom: 7,
              }}
            >
              "{lastUserMsg.message?.slice(0, 90)}
              {lastUserMsg.message?.length > 90 ? "…" : ""}"
            </div>
          )}

          {/* Row 3: timestamps */}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {[
              { label: "Created", value: fmtDate(session.created_at) },
              {
                label: "Updated",
                value: fmtDate(session.updated_at),
                dim: relTime(session.updated_at),
              },
            ].map(({ label, value, dim }) => (
              <div
                key={label}
                style={{ display: "flex", alignItems: "center", gap: 5 }}
              >
                <span style={{ fontSize: 9, color: T.textDim }}>{label}</span>
                <span
                  style={{
                    fontSize: 9,
                    color: T.textSecondary,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {value}
                </span>
                {dim && (
                  <span style={{ fontSize: 9, color: T.textDim }}>({dim})</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right side: delete + arrow */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
            marginTop: 4,
          }}
        >
          {/* Delete button */}
          <button
            onClick={(e) => {
              e.stopPropagation(); // prevent card open
              onDelete(session.sessionId);
            }}
            title="Delete session"
            style={{
              background: "none",
              border: `1px solid transparent`,
              borderRadius: T.radiusSm,
              color: T.textDim,
              cursor: "pointer",
              padding: "3px 6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "color 0.15s, border-color 0.15s, background 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = T.redText;
              e.currentTarget.style.borderColor = T.redBorder;
              e.currentTarget.style.background = T.redDim;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = T.textDim;
              e.currentTarget.style.borderColor = "transparent";
              e.currentTarget.style.background = "none";
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
            </svg>
          </button>

          {/* Arrow */}
          <span className="arrow" style={{ fontSize: 18 }}>
            ›
          </span>
        </div>
      </div>
    </div>
  );
}

// ── SkeletonCard ─────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div
      style={{
        background: T.bg2,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusLg,
        padding: 16,
      }}
    >
      <div style={{ display: "flex", gap: 10 }}>
        <Skeleton
          w={32}
          h={32}
          style={{ borderRadius: T.radius, flexShrink: 0 }}
        />
        <div
          style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}
        >
          <Skeleton w="40%" h={12} />
          <Skeleton w="65%" h={10} />
          <Skeleton w="50%" h={9} />
        </div>
      </div>
    </div>
  );
}

// ── HistoryControls ──────────────────────────────────────────────
function HistoryControls({ search, setSearch, sortBy, setSortBy }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 12, flexShrink: 0 }}>
      {/* Search */}
      <div
        style={{
          flex: 1,
          background: T.bg2,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          padding: "8px 11px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill={T.textDim}>
          <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
        </svg>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search sessions or messages…"
          style={{
            flex: 1,
            background: "none",
            border: "none",
            outline: "none",
            fontSize: 11,
            color: T.textPrimary,
            fontFamily: T.fontMono,
          }}
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            style={{
              background: "none",
              border: "none",
              color: T.textDim,
              cursor: "pointer",
              fontSize: 14,
              padding: 0,
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Sort */}
      <select
        className="sort-select"
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
      >
        <option value="updated">Recent activity</option>
        <option value="created">Date created</option>
        <option value="messages">Most messages</option>
      </select>
    </div>
  );
}

// ── History Page ─────────────────────────────────────────────────
export default function History({ onOpenSession, onSessionDeleted }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("updated");
  const [deletingId, setDeletingId] = useState(null);

  console.log("User ID:", USER_ID);
  console.log("API Base URL:", API_BASE);

  useEffect(() => {
    fetch(`${API_BASE}/sessions/${USER_ID}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setSessions(data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  const filtered = sessions
    .filter((s) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        s.sessionId?.toLowerCase().includes(q) ||
        (s.messages || []).some((m) => m.message?.toLowerCase().includes(q))
      );
    })
    .sort((a, b) => {
      if (sortBy === "updated")
        return new Date(b.updated_at) - new Date(a.updated_at);
      if (sortBy === "created")
        return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === "messages")
        return (b.messages?.length || 0) - (a.messages?.length || 0);
      return 0;
    });

  const handleDelete = async (sessionId) => {
    if (!window.confirm(`Delete session #${sessionId}?`)) return;
    setDeletingId(sessionId);
    try {
      const res = await fetch(`${API_BASE}/sessions/${USER_ID}/${sessionId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
    } catch (e) {
      alert(`Failed to delete: ${e.message}`);
    } finally {
      setDeletingId(null);
      localStorage.removeItem("activeSession");
      onSessionDeleted(sessionId);
    }
  };

  // in the filtered.map() render:
  filtered.map((s, i) => (
    <SessionCard
      key={s.id || s.sessionId}
      session={s}
      onOpen={onOpenSession}
      onDelete={handleDelete} // ← add this
      index={i}
    />
  ));

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: T.bg0,
      }}
    >
      {/* Page header */}
      <div
        style={{
          background: T.bg1,
          borderBottom: `1px solid ${T.border}`,
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: T.radius,
            background: T.greenDim,
            border: `1px solid ${T.greenBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill={T.green}>
            <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: T.textPrimary }}>
            Chat History
          </div>
          <div style={{ fontSize: 10, color: T.textDim }}>
            {USER_ID} · {sessions.length} session
            {sessions.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          padding: "14px 16px",
        }}
      >
        {!loading && !error && sessions.length > 0 && (
          <StatsBar sessions={sessions} />
        )}

        <HistoryControls
          search={search}
          setSearch={setSearch}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />

        {/* Session list */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {loading &&
            Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}

          {error && (
            <div
              style={{
                background: T.redDim,
                border: `1px solid ${T.redBorder}`,
                borderRadius: T.radius,
                padding: "14px 16px",
                fontSize: 11,
                color: T.redText,
              }}
            >
              <div style={{ fontWeight: 500, marginBottom: 4 }}>
                Failed to load sessions
              </div>
              <div style={{ color: "#b06060", marginBottom: 6 }}>{error}</div>
              <code
                style={{
                  fontSize: 10,
                  background: "#3a1515",
                  padding: "2px 6px",
                  borderRadius: 3,
                }}
              >
                GET /api/sessions/{USER_ID}
              </code>
              <span style={{ fontSize: 10, color: "#904040" }}>
                {" "}
                must be available on localhost:8080
              </span>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div
              style={{
                textAlign: "center",
                color: T.textDim,
                fontSize: 12,
                paddingTop: 60,
              }}
            >
              {search
                ? `No sessions matching "${search}"`
                : "No sessions found"}
            </div>
          )}

          {!loading &&
            !error &&
            filtered.map((s, i) => (
              <SessionCard
                key={s.id || s.sessionId}
                session={s}
                onOpen={onOpenSession}
                onDelete={handleDelete}
                index={i}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
